// =============================================================================
// resourceTree.ts — pure tree logic for Resources content pages (db-109)
// =============================================================================
// Turns the flat `resource_pages` rows (parent_id + sort_order) into a nestable
// tree for the sidebar, and provides the helpers the Manage editor needs
// (subtree ids for cascade-delete, cycle detection when re-parenting). No I/O.
// =============================================================================

export interface ResourceNodeInput {
  id: string;
  parent_id: string | null;
  title: string;
  sort_order: number;
}

export interface ResourceTreeNode<T> {
  node: T;
  children: ResourceTreeNode<T>[];
  depth: number;
}

// Build the tree. Each parent's children are ordered by sort_order then title.
// A row whose parent_id is missing from the set is treated as top-level, so
// nothing ever disappears; cycles are broken defensively (a node is placed once).
export function buildResourceTree<T extends ResourceNodeInput>(rows: T[]): ResourceTreeNode<T>[] {
  const ids = new Set((rows || []).map((r) => r.id));
  const childrenOf = new Map<string | null, T[]>();
  for (const r of rows || []) {
    const key = r.parent_id && ids.has(r.parent_id) ? r.parent_id : null;
    const list = childrenOf.get(key) || [];
    list.push(r);
    childrenOf.set(key, list);
  }
  for (const list of childrenOf.values()) {
    list.sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title));
  }
  const placed = new Set<string>();
  function build(parentKey: string | null, depth: number): ResourceTreeNode<T>[] {
    const out: ResourceTreeNode<T>[] = [];
    for (const r of childrenOf.get(parentKey) || []) {
      if (placed.has(r.id)) continue; // cycle / double-parent guard
      placed.add(r.id);
      out.push({ node: r, children: build(r.id, depth + 1), depth });
    }
    return out;
  }
  return build(null, 0);
}

// Depth-first flatten to display order, carrying depth (for indented rendering).
export function flattenResourceTree<T>(tree: ResourceTreeNode<T>[]): { node: T; depth: number }[] {
  const out: { node: T; depth: number }[] = [];
  (function walk(nodes: ResourceTreeNode<T>[]) {
    for (const n of nodes) {
      out.push({ node: n.node, depth: n.depth });
      walk(n.children);
    }
  })(tree);
  return out;
}

// All descendant ids of a page (for cascade soft-delete of a subtree).
export function descendantIds<T extends ResourceNodeInput>(rows: T[], id: string): string[] {
  const childrenOf = new Map<string, T[]>();
  for (const r of rows || []) {
    if (!r.parent_id) continue;
    const list = childrenOf.get(r.parent_id) || [];
    list.push(r);
    childrenOf.set(r.parent_id, list);
  }
  const out: string[] = [];
  const seen = new Set<string>([id]);
  const stack = [id];
  while (stack.length > 0) {
    const cur = stack.pop() as string;
    for (const child of childrenOf.get(cur) || []) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      out.push(child.id);
      stack.push(child.id);
    }
  }
  return out;
}

// Ancestor page titles from root → the given page's PARENT (excludes the page
// itself), for a "Section › Parent › Page" breadcrumb. Cycle-safe.
export function ancestorTitles<T extends ResourceNodeInput>(rows: T[], id: string): string[] {
  const byId = new Map<string, T>();
  for (const r of rows || []) byId.set(r.id, r);
  const chain: string[] = [];
  const seen = new Set<string>([id]);
  let cur = byId.get(id);
  while (cur?.parent_id && byId.has(cur.parent_id) && !seen.has(cur.parent_id)) {
    seen.add(cur.parent_id);
    const parent = byId.get(cur.parent_id) as T;
    chain.unshift(parent.title);
    cur = parent;
  }
  return chain;
}

// Would parenting `pageId` under `candidateParent` create a cycle? A page can't
// be its own parent or be parented to one of its descendants.
export function wouldCreateCycle<T extends ResourceNodeInput>(
  rows: T[],
  pageId: string,
  candidateParent: string | null
): boolean {
  if (!candidateParent) return false;
  if (candidateParent === pageId) return true;
  return descendantIds(rows, pageId).includes(candidateParent);
}
